import React, { Fragment, FunctionComponent, ReactElement, useCallback, useEffect, useRef, useState } from "react";

import { useClickOutsideModalWatcher } from "@multiweb/reactUtils";
import { DropdownLoadingProps, DropdownSizesProps } from "../../types";
import { DropdownComponent } from "../DropdownComponent/DropdownComponent";
import { DropdownTriggerRenderer, OpenOn } from "./types";

export type DropdownProps = {
    /**
     * Potomci k vyrenderování.
     * Měly by to být být DropdownItem, DropdownListItem,
     * DropdownMultiButtonsItem nebo DropdownSeparatorItem.
     */
    children: ReactElement<any>[];
    /** Typ otevírání Dropdownu. */
    openOn?: OpenOn;
    /**
     * Renderer pro trigger Dropdownu. Pro správné fungování musí předat
     * všechny předané properties (onClick, onKeyDown, ...).
     */
    triggerRenderer: DropdownTriggerRenderer;
    /** Callback volající se v případě změny viditelnosti dropdownu. */
    onVisibilityChange?: (isOpen: boolean) => void;
} & DropdownSizesProps & DropdownLoadingProps;

export const Dropdown: FunctionComponent<DropdownProps> = ({
    children,
    openOn = OpenOn.click,
    triggerRenderer,
    isLoading,
    loaderRenderer,
    height,
    maxHeight,
    width,
    maxWidth,
    onVisibilityChange,
}) => {
    const triggerElement = useRef<HTMLDivElement>(null);
    const contextElement = useRef<HTMLDivElement>(null);
    const [isOpen, setIsOpen] = useState(false);

    const handleVisibility = useCallback((isOpen: boolean) => {
        setIsOpen(isOpen);
        if (onVisibilityChange) {
            onVisibilityChange(isOpen);
        }
    }, [onVisibilityChange]);

    const clickOutsideCallback = useCallback(() => handleVisibility(false), [handleVisibility]);

    useClickOutsideModalWatcher(triggerElement, contextElement, clickOutsideCallback);

    useEffect(() => {
        const listenerElement = triggerElement.current;
        const hoverListener = () => handleVisibility(true);
        if (openOn === OpenOn.hover || openOn === OpenOn.clickAndHover) {
            listenerElement?.addEventListener("mouseenter", hoverListener);
        }

        return () => {
            listenerElement?.removeEventListener("mouseenter", hoverListener);
        };
    }, [openOn, triggerElement, handleVisibility]);

    return (
        <Fragment>
            <div ref={triggerElement}>
                {triggerRenderer({
                    onClick: (e) => {
                        e.stopPropagation();
                        if (isOpen || openOn === OpenOn.click || openOn === OpenOn.clickAndHover) {
                            handleVisibility(!isOpen);
                        }
                    },
                    onKeyDown: (e) => {
                        if (e.key === "Enter") {
                            handleVisibility(!isOpen);
                        }
                    },
                })}
            </div>
            <DropdownComponent {...{ isOpen, isLoading, loaderRenderer, setIsOpen, triggerElement, contextElement, height, width, maxHeight, maxWidth }}>
                {children}
            </DropdownComponent>
        </Fragment>
    );
};
